// Spanish (es / es-419) resource bundle for the patient portal.
// Wording reuses the sibling mobile app's es-419 translations where strings
// overlap (see patient-app/src/i18n/LocalizationProvider.tsx) so terminology
// stays consistent across products. Country-general LATAM Spanish.
import type { PortalTranslations } from './en';

const es: PortalTranslations = {
  common: {
    patient: 'Paciente',
    careTeam: 'Equipo de atención',
    appointment: 'Cita',
    document: 'Documento',
    telemedicine: 'Telemedicina',
    virtualVisit: 'Visita virtual',
    loading: 'Cargando...',
    na: 'N/D',
    notAssigned: 'No asignado',
    refresh: 'Actualizar',
    refreshing: 'Actualizando...',
    selfAccess: 'Acceso propio',
    me: 'Yo',
    total: 'en total',
    active: 'activas',
    yes: 'Sí',
    no: 'No',
    close: 'Cerrar',
    reset: 'Restablecer',
    cancel: 'Cancelar',
  },
  nav: {
    groups: {
      dashboard: 'Panel',
      medical: 'Médico',
      finance: 'Finanzas',
      understand: 'Información',
    },
    items: {
      home: 'Inicio',
      appointments: 'Citas',
      messages: 'Mensajes',
      contacts: 'Contactos',
      labs: 'Resultados de laboratorio',
      records: 'Registros médicos',
      prescriptions: 'Recetas',
      'care-plans': 'Planes de atención',
      forms: 'Formularios',
      billing: 'Facturación',
      history: 'Historial',
      reports: 'Informes',
      webmd: 'WebMD',
      support: 'Soporte',
      settings: 'Configuración',
      logout: 'Cerrar sesión',
    },
    brandSub: 'Portal del paciente',
  },
  layout: {
    loadingPortal: 'Cargando el portal del paciente...',
    searchPlaceholder: 'Buscar citas, mensajes, registros, recetas...',
    activePatient: 'Paciente activo',
    mrn: 'MRN {{mrn}}',
    lastRefresh: 'Última actualización {{value}}',
    search: 'Búsqueda "{{query}}"',
  },
  home: {
    description:
      'Acceso unificado a citas, formularios, facturación, informes de laboratorio y comunicación con el equipo de atención.',
    workspaceFallback: 'Espacio del paciente',
    metrics: {
      upcomingVisits: 'Próximas visitas',
      formsPending: 'Formularios pendientes',
      openInvoices: 'Facturas abiertas',
      activeMeds: 'Medicamentos activos',
      labReports: 'Informes de laboratorio',
      outstanding: 'Pendiente',
    },
    formsToComplete_one: '{{count}} formulario por completar',
    formsToComplete_other: '{{count}} formularios por completar',
    formsToCompleteBody: 'Completa los formularios de admisión para preparar tu visita',
    docsNeedAttention_one: '{{count}} documento necesita atención',
    docsNeedAttention_other: '{{count}} documentos necesitan atención',
    docsNeedAttentionBody: 'Vuelve a subir o corrige los documentos señalados',
    moreItems: '+{{count}} más',
    recentActivity: 'Actividad reciente',
    loadingActivity: 'Cargando actividad...',
    noRecentActivity: 'No se encontró actividad reciente.',
    upcomingSnapshot: 'Resumen de próximas citas',
    tableType: 'Tipo',
    tableDateTime: 'Fecha/Hora',
    tableProvider: 'Profesional',
    tableStatus: 'Estado',
    noUpcoming: 'No hay citas próximas',
    loadError: 'No se pudo cargar el panel del paciente.',
  },
  appointments: {
    title: 'Citas',
    description: 'Agenda de visitas, asignación de profesionales y disponibilidad de telemedicina.',
    loadError: 'No se pudieron cargar las citas.',
    bookNextTitle: 'Reserva tu próxima visita',
    bookNextBody:
      'Solicita una visita que la clínica confirma desde el EMR, o únete a una lista de espera del mismo día cuando un doctor acepte horarios de último momento.',
    bookAppointment: 'Reservar una cita',
    joinWaitlist: 'Unirse a lista de espera de último momento',
    searchPlaceholder: 'Buscar por tipo, profesional o telemedicina',
    status: {
      all: 'Todos los estados',
      scheduled: 'Programada',
      portalBooked: 'Reservada en el portal',
      checkedIn: 'Registrado',
      completed: 'Completada',
      cancelled: 'Cancelada',
      noShow: 'No asistió',
      waiting: 'En espera',
      seeDoctorSoon: 'Verá al doctor pronto',
    },
    upcomingRecent: 'Visitas próximas y recientes',
    selectToSeeDetails: 'Selecciona una cita para ver detalles y acciones.',
    loading: 'Cargando citas...',
    none: 'No se encontraron citas.',
    bookedInPortal: 'Reservada en el portal',
    bookedByStaff: 'Reservada por el personal',
    detailEyebrow: 'Detalles de la cita',
    selectToView: 'Selecciona una cita para ver los detalles.',
    provider: 'Profesional',
    portalBookedNote:
      'Esta cita se solicitó desde el portal y aún espera confirmación por parte del EMR.',
    telemedReadiness: 'Disponibilidad de telemedicina',
    joinLinkReady: 'El enlace para unirse está listo.',
    joinLinkPending: 'El enlace para unirse aparecerá cuando la sesión esté preparada.',
    waitlistsEyebrow: 'Listas de espera de último momento',
    waitlistsSub: 'Tus entradas actuales en listas de espera del portal y el estado de la cola.',
    earlierSlot: 'Hay un horario más temprano disponible',
    expires: 'Vence {{value}}',
    accept: 'Aceptar',
    accepting: 'Aceptando...',
    noWaitlists: 'No estás en ninguna lista de espera de doctores en este momento.',
    queue: 'Cola n.º {{number}}',
    rank: 'Posición {{rank}}',
    beReadyBy: 'Prepárate antes de {{value}}',
    actions: 'Acciones',
    joinVirtual: 'Unirse a la visita virtual',
    messageCareTeam: 'Enviar mensaje al equipo de atención',
    reschedule: 'Reprogramar cita',
    cancel: 'Cancelar cita',
    cancelling: 'Cancelando...',
    cancelConfirm:
      '¿Cancelar esta cita? Se notificará a tu equipo de atención y es posible que debas solicitar un nuevo horario.',
    cancelError: 'No se pudo cancelar la cita.',
    portalLabel: 'Portal',
    staffLabel: 'Personal',
    // Textos alternativos de los formateadores de fecha / ubicación.
    timeTbd: 'Horario por confirmar',
    tbd: 'Por confirmar',
    virtualVisit: 'Visita virtual',
    locationTbd: 'Ubicación por confirmar',
    slots: '{{count}} horarios',
    durationMin: '{{count}} min',
    // Confirmaciones y errores de los flujos de reserva / lista de espera.
    waitlistJoined: 'Te uniste a la lista de espera de último momento de {{provider}} para el {{date}}.',
    waitlistJoinError: 'No se pudo unir a la lista de espera.',
    offerAcceptedConfirm: 'Cita más temprana aceptada para el {{date}}.',
    offerAcceptError: 'No se pudo aceptar esta oferta de lista de espera.',
    rescheduledConfirm: 'Cita reprogramada para el {{date}}.',
    instantConfirmed: 'Cita inmediata confirmada para el {{date}}.',
    requestSentConfirm:
      'Solicitud de cita enviada para el {{date}}. Pronto recibirás un correo de confirmación.',
    bookingError: 'No se pudo confirmar esta reserva.',
    loadProvidersError: 'No se pudieron cargar los doctores disponibles para ese día.',
    loadProviderDayError: 'No se pudieron cargar los horarios reservables en el portal para este doctor.',
    loadWaitlistError: 'No se pudieron cargar los doctores con lista de espera para ese día.',
    booking: {
      instantBadge: 'Reserva inmediata',
      requestBadge: 'Solicitud de cita',
      rescheduleTitle: 'Reprogramar cita',
      instantTitle: 'Reservar una cita inmediata',
      requestTitle: 'Reservar una cita',
      instantSubtitle: 'Elige un horario disponible en el portal y confírmalo de inmediato.',
      requestSubtitle:
        'Elige un día, selecciona un doctor disponible y envía la solicitud al EMR para su confirmación.',
      step1: '1. Selecciona una fecha',
      step1Hint: 'Los doctores disponibles se actualizan según el día seleccionado.',
      step2: '2. Selecciona un doctor',
      loadingDoctors: 'Cargando doctores disponibles...',
      noDoctors: 'Ningún doctor tiene horarios reservables en el portal para esa fecha.',
      step3: '3. Selecciona un horario',
      loadingTimes: 'Cargando horarios disponibles...',
      chooseDoctorFirst: 'Primero elige un doctor.',
      noTimes: 'No hay horarios reservables en el portal para este doctor ese día.',
      step4: '4. Selecciona un tipo de cita',
      chooseTimeFirst: 'Elige un horario para ver los tipos de cita visibles en el portal para ese espacio.',
      step5: '5. Confirma los detalles',
      doctor: 'Doctor',
      selectDoctor: 'Selecciona un doctor',
      time: 'Horario',
      selectTime: 'Selecciona un horario',
      appointmentType: 'Tipo de cita',
      selectAppointmentType: 'Selecciona un tipo de cita',
      visitEnd: 'Fin de la visita',
      rescheduleNote: 'Esto reemplazará tu cita actual por el nuevo horario seleccionado.',
      instantNote: 'La reserva inmediata confirma el horario del portal seleccionado al instante.',
      requestNote:
        'Esta solicitud ingresará al EMR como Reservada en el portal y esperará la confirmación de la clínica. Se enviará un resumen por correo después del envío.',
      confirming: 'Confirmando...',
      confirmReschedule: 'Confirmar reprogramación',
      confirmInstant: 'Confirmar cita inmediata',
      sendRequest: 'Enviar solicitud de cita',
    },
    waitlistDialog: {
      badge: 'Lista de espera de último momento',
      title: 'Unirse a la lista de espera de un doctor',
      subtitle:
        'Mira qué doctores tienen lista de espera habilitada para el día seleccionado, únete a la cola y sigue tu posición desde el portal.',
      selectDay: 'Selecciona un día',
      selectDayHint: 'Aquí solo aparecerán los doctores con una lista de espera activa ese día.',
      loadingDoctors: 'Cargando doctores con lista de espera...',
      noDoctors: 'Ningún doctor tiene una lista de espera activa para ese día.',
      cutoff: 'Cierre {{value}}',
      joinedCount: '{{count}}/{{max}} inscritos',
      alreadyJoinedTitle: 'Ya te uniste a esta cola.',
      alreadyJoinedDetail: 'Cola n.º {{number}}',
      rankSuffix: ' • Posición {{rank}}',
      readyBySuffix: ' • Prepárate antes de {{value}}',
      notePlaceholder: 'Nota opcional para la clínica',
      alreadyJoined: 'Ya inscrito',
      waitlistFull: 'Lista de espera llena',
      join: 'Unirse a la lista de espera',
    },
  },
  billing: {
    title: 'Facturación',
    description: 'Facturas, fechas de vencimiento, pagos registrados y saldos pendientes.',
    loadError: 'No se pudo cargar la facturación.',
    invoice: 'Factura',
    date: 'Fecha',
    dueDate: 'Fecha de vencimiento',
    balance: 'Saldo',
    total: 'Total',
    status: 'Estado',
    payNow: 'Pagar ahora',
    starting: 'Iniciando…',
    noEntries: 'No hay registros de facturación',
    checkoutError: 'El pago en línea no está disponible en este momento.',
    checkoutMissingUrl: 'No se pudo iniciar el pago. Inténtalo de nuevo.',
  },
  records: {
    title: 'Registros médicos',
    description:
      'Sube documentos de respaldo y haz seguimiento del estado de revisión de tu equipo de atención.',
    loadError: 'No se pudieron cargar los registros médicos.',
    uploadDocument: 'Subir documento',
    noDocumentTypes:
      'Tu equipo de atención aún no ha configurado tipos de documento. Contacta a tu profesional para habilitar las cargas de documentos.',
    documentType: 'Tipo de documento',
    file: 'Archivo',
    fileHint: 'Permitidos: PDF, JPG, PNG, HEIC. Tamaño máximo: 25 MB.',
    titleLabel: 'Título',
    titlePlaceholder: 'p. ej. Frente de la tarjeta de seguro',
    descriptionLabel: 'Descripción',
    descriptionPlaceholder: 'Notas opcionales para el personal que revise este archivo',
    sizeLabel: 'Tamaño: {{value}}',
    mimeLabel: 'Tipo MIME: {{value}}',
    unknown: 'Desconocido',
    uploading: 'Subiendo...',
    selectTypeAndFile: 'Selecciona un tipo de documento y un archivo antes de subir.',
    uploadSuccess: 'Documento subido correctamente.',
    uploadError: 'No se pudo subir el documento.',
    library: 'Biblioteca de documentos',
    loadingDocuments: 'Cargando documentos...',
    libraryEmpty:
      'Aún no hay registros disponibles. Sube una referencia, tarjeta de seguro o informe externo para comenzar.',
    uploaded: 'Subido:',
    reviewed: 'Revisado:',
    fileSize: 'Tamaño del archivo:',
    source: 'Origen:',
    reviewNote: 'Nota de revisión:',
  },
  forms: {
    title: 'Formularios',
    description: 'Completa cuestionarios de admisión, consentimiento y seguimiento directamente en el portal.',
    loadError: 'Error al cargar formularios.',
    pending: 'Pendientes ({{count}})',
    completed: 'Completados ({{count}})',
    completedForms: 'Formularios completados',
    pendingForms: 'Formularios pendientes',
    loading: 'Cargando formularios...',
    noCompleted: 'Aún no hay formularios completados.',
    noPending: 'No hay formularios pendientes asignados.',
    formFallback: 'Formulario',
    generalCategory: 'General',
    completion: 'Progreso',
    due: 'Vence',
    review: 'Revisión',
    detail: 'Detalle del formulario',
    loadingForm: 'Cargando formulario...',
    selectForm: 'Selecciona un formulario para ver sus preguntas y completarlo aquí.',
    summary: 'Resumen del formulario',
    status: 'Estado',
    completed_label: 'Completado:',
    reviewed: 'Revisado:',
    signatureRequired: 'Firma requerida:',
    estimatedTime: 'Tiempo estimado:',
    minutes: '{{count}} min',
    publicLinkFallback: 'Hay un enlace público alternativo disponible para este formulario.',
    openExternal: 'Abrir vista externa',
    definitionUnavailable: 'Esta definición de formulario no está disponible para mostrarse en el portal.',
    signatureNote:
      'Escribir tu nombre legal en el campo de firma cuenta como la firma del paciente para el envío.',
    saveProgressNote: 'Puedes guardar el progreso y volver más tarde.',
    reset: 'Restablecer',
    changesReset: 'Cambios restablecidos al último estado guardado.',
    saveDraft: 'Guardar borrador',
    saving: 'Guardando...',
    draftSaved: 'Borrador guardado.',
    saveDraftError: 'No se pudo guardar el borrador.',
    submit: 'Enviar formulario',
    submitting: 'Enviando...',
    submitSuccess: 'Formulario enviado correctamente.',
    submitError: 'No se pudo enviar el formulario.',
    detailLoadError: 'No se pudo cargar el detalle del formulario.',
  },
  settings: {
    title: 'Configuración',
    description: 'Preferencias del portal, visibilidad de contextos e información de la cuenta.',
    account: 'Cuenta',
    displayName: 'Nombre para mostrar:',
    email: 'Correo electrónico:',
    defaultProvider: 'Profesional predeterminado:',
    contextAccess: 'Acceso por contexto',
    contextId: 'ID de contexto:',
    language: 'Idioma',
    languageHint: 'Elige el idioma que se usa en todo el portal del paciente.',
    languageEn: 'Inglés',
    languageEs: 'Español',
    security: 'Seguridad',
    changePassword: 'Cambiar contraseña',
  },
  changePassword: {
    title: 'Actualiza tu contraseña',
    subtitle: 'Por tu seguridad, debes establecer una nueva contraseña antes de continuar.',
    currentPassword: 'Contraseña actual',
    newPassword: 'Nueva contraseña',
    confirmPassword: 'Confirmar nueva contraseña',
    policyHint:
      'Usa al menos 12 caracteres con una letra mayúscula, una minúscula, un número y un símbolo.',
    submit: 'Actualizar contraseña',
    submitting: 'Actualizando...',
    mismatch: 'La nueva contraseña y la confirmación no coinciden.',
    sameAsCurrent: 'La nueva contraseña debe ser diferente de la actual.',
    policyError:
      'La contraseña debe tener al menos 12 caracteres e incluir mayúscula, minúscula, un número y un símbolo.',
    fillAll: 'Ingresa tu contraseña actual y escribe la nueva contraseña dos veces.',
    genericError: 'No se pudo actualizar la contraseña.',
    success: 'Contraseña actualizada. Inicia sesión nuevamente con tu nueva contraseña.',
    signOut: 'Cerrar sesión',
  },
  messages: {
    title: 'Mensajes',
    description: 'Revisa conversaciones seguras con tus equipos clínico y de facturación.',
    notEnabled: 'Este contexto de paciente no tiene la mensajería segura habilitada.',
    sidebarTitle: 'Mensajes',
    sidebarSubtitle: 'Comunicación segura para el contexto del paciente activo.',
    searchPlaceholder: 'Buscar asuntos o mensajes...',
    filterAll: 'Todos',
    filterOpen: 'Abiertos',
    filterClosed: 'Cerrados',
    subjectPlaceholder: 'Asunto',
    messagePlaceholder: 'Escribe tu mensaje...',
    newConversation: 'Nueva conversación',
    creating: 'Creando...',
    loadingConversations: 'Cargando conversaciones...',
    noMatches: 'Ninguna conversación coincide con tu filtro actual.',
    careTeam: 'Equipo de atención',
    noMessagesYet: 'Aún no hay mensajes.',
    you: 'Tú',
    replyPlaceholder: 'Escribe una respuesta segura...',
    send: 'Enviar',
    sending: 'Enviando...',
    loadingMessages: 'Cargando mensajes...',
    selectConversation: 'Selecciona una conversación para empezar a chatear.',
    loadError: 'No se pudieron cargar los mensajes.',
    detailLoadError: 'No se pudo cargar la conversación.',
    sendError: 'No se pudo enviar el mensaje.',
    createError: 'No se pudo crear la conversación.',
  },
  contacts: {
    title: 'Contactos y perfil',
    description: 'Datos demográficos del paciente y directorio del equipo de atención asignado.',
    assignedProvider: 'Profesional asignado',
    phoneViaMessages: 'Usa los mensajes seguros',
    emailViaMessages: 'Contacta por los mensajes del portal',
    patientProfile: 'Perfil del paciente',
    name: 'Nombre:',
    mrn: 'MRN:',
    dob: 'Fecha de nacimiento:',
    email: 'Correo electrónico:',
    phone: 'Teléfono:',
    status: 'Estado:',
    statusActive: 'Activo',
    provider: 'Profesional:',
    headerName: 'Nombre',
    headerRole: 'Rol',
    headerPhone: 'Teléfono',
    headerEmail: 'Correo electrónico',
    noContacts: 'No hay contactos del equipo de atención disponibles',
  },
  history: {
    title: 'Historial',
    description: 'Flujo cronológico de visitas, laboratorios, facturación y mensajes.',
    headerType: 'Tipo',
    headerTitle: 'Título',
    headerDateTime: 'Fecha/Hora',
    headerDetail: 'Detalle',
    headerStatus: 'Estado',
    empty: 'No se encontraron eventos en el historial',
    loadError: 'No se pudo cargar el historial.',
  },
  reports: {
    title: 'Informes',
    description: 'Resumen financiero y métricas rápidas de la cuenta.',
    totalInvoicedYtd: 'Total facturado en el año',
    totalPaidYtd: 'Total pagado en el año',
    outstandingBalance: 'Saldo pendiente',
    reportNotes: 'Notas del informe',
    patient: 'Paciente:',
    invoicesThisYear: 'Facturas de este año:',
    outstanding: 'Saldo pendiente:',
    loadError: 'No se pudieron cargar los informes.',
  },
  support: {
    title: 'Soporte',
    description: 'Datos de contacto para coordinación técnica, de facturación y de atención urgente.',
    portalSupport: 'Soporte del portal',
    portalSupportLine: 'Usa los mensajes seguros de este portal',
    portalSupportBody:
      'Los problemas de acceso a la cuenta, inicio de sesión y carga de documentos deben enviarse desde la página de Mensajes.',
    billingSupport: 'Soporte de facturación',
    yourCareTeam: 'Tu equipo de atención',
    billingSupportBody:
      'Las preguntas sobre facturas, registro de pagos y seguros pueden dirigirse a tu equipo de atención asignado.',
    urgentCare: 'Guía para atención urgente',
    urgentCareLine: 'Llama directamente a la clínica',
    urgentCareBody: 'Los mensajes del portal no se monitorean para emergencias.',
  },
  labResults: {
    title: 'Resultados de laboratorio',
    description: 'Actividad de laboratorio reciente, estados de los informes y valores señalados.',
    headerPanel: 'Panel',
    headerCollected: 'Tomado',
    headerOrderingProvider: 'Profesional solicitante',
    headerAbnormal: 'Anormales',
    headerStatus: 'Estado',
    seeCareTeamNotes: 'Consulta las notas del equipo de atención',
    empty: 'No hay informes de laboratorio disponibles',
    loadError: 'No se pudieron cargar los resultados de laboratorio.',
  },
  carePlans: {
    title: 'Planes de atención',
    description: 'Hitos del plan, puntaje de progreso y próxima fecha de revisión clínica.',
    empty: 'No hay planes de atención asignados.',
    carePlanFallback: 'Plan de atención',
    progress: 'Progreso',
    nextReview: 'Próxima revisión',
    loadError: 'No se pudieron cargar los planes de atención.',
  },
  prescriptions: {
    title: 'Recetas',
    description: 'Medicamentos activos, horario de dosis y disponibilidad de resurtido.',
    headerMedication: 'Medicamento',
    headerDosage: 'Dosis',
    headerFrequency: 'Frecuencia',
    headerRefills: 'Resurtidos',
    headerPharmacy: 'Farmacia',
    headerStatus: 'Estado',
    headerAction: 'Acción',
    prescribedBy: 'Recetado por {{name}}',
    noPharmacy: 'Sin farmacia asignada',
    requestRefill: 'Solicitar resurtido',
    requesting: 'Solicitando...',
    pharmacyRequired: 'Debe asignarse una farmacia antes de poder solicitar un resurtido.',
    latestRequest: 'Última solicitud: {{status}} el {{date}}',
    noRequestsYet: 'Aún no hay solicitudes de resurtido',
    empty: 'No hay recetas disponibles',
    refillFormTitle: 'Solicitud de resurtido para {{drug}}',
    requestedRefills: 'Resurtidos solicitados',
    currentPrescription: 'Receta actual',
    medicationDetailsPending: 'Detalles del medicamento pendientes',
    scheduleNotPosted: 'Horario no publicado',
    pharmacyNote: 'Nota para la farmacia',
    pharmacyNotePlaceholder:
      'Agrega contexto para tu equipo de atención o farmacia, como fechas de viaje o problemas de suministro.',
    submitRequest: 'Enviar solicitud de resurtido',
    submitting: 'Enviando...',
    assignedPharmacy: 'Farmacia asignada',
    phoneUnavailable: 'Número de teléfono no disponible',
    recentRequests: 'Solicitudes de resurtido recientes',
    requestedRefillsCount: 'Resurtidos solicitados: {{count}}',
    noRequestsForPrescription: 'Aún no se han enviado solicitudes de resurtido para esta receta.',
    recentActivity: 'Actividad de resurtido reciente',
    requestedViaPharmacy: 'Se solicitaron {{count}} resurtido(s) a través de {{pharmacy}}',
    noActivity: 'Aún no se han enviado solicitudes de resurtido.',
    loadError: 'No se pudieron cargar las recetas.',
    minRefillsError: 'Los resurtidos solicitados deben ser al menos 1.',
    submitSuccess: 'Solicitud de resurtido enviada. Tu equipo de atención la revisará.',
    submitError: 'No se pudo enviar la solicitud de resurtido.',
  },
  webmd: {
    title: 'Recursos de WebMD',
    description: 'Referencias educativas confiables relacionadas con tu plan de atención actual.',
    suggestedReading: 'Lecturas sugeridas',
    empty: 'Aún no hay temas educativos específicos para el paciente.',
  },
};

export default es;
